import React, { useState, useEffect, useCallback } from 'react';
import { Input, Separator } from '@roger/ui-components';
import { GripVertical, Loader2, X } from 'lucide-react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  listVisitDiagnosesVisitsVisitIdDiagnosesGet,
  createVisitDiagnosisVisitsVisitIdDiagnosesPost,
  updateVisitDiagnosisVisitsDiagnosesDiagnosisIdPut,
  deleteVisitDiagnosisVisitsDiagnosesDiagnosisIdDelete,
  updateDiagnosisPositionsVisitsVisitIdDiagnosesPositionsPut,
} from '../../services/api/sdk.gen';
import type {
  PrimaryDiagnosisFitness,
  VisitDiagnosisResponse,
} from '../../services/api/types.gen';
import DraggableGrid from '../DraggableGrid';
import InlineDiagnosisSearch from './InlineDiagnosisSearch';
import InlineDiagnosisFields from './InlineDiagnosisFields';
import PrimaryFitnessConfirmDialog from '../PrimaryFitnessConfirmDialog';
import { DiagnosisCodeValidityWarning } from '../DiagnosisCodeValidityWarning';
import { useToast } from '../../hooks/useToastContext';
import { getUserFacingApiMessage } from '../../utils/apiErrorDetail';
import { OASISActiveDiagnoses } from './OASISActiveDiagnoses';
import { contradictionAnchor } from './contradictions/contradictionNavigation';
import { parseISOToLocalDate, formatDateToISO } from '../../utils/dateUtils';
import type { OEType, SeverityType } from '../../types/codingData.types';
import {
  fitnessPresentation,
  isPdgmRelevant,
  shouldDiscourage,
} from '../../utils/primaryDiagnosisFitness';

const MAX_TOTAL = 25;

/**
 * A seat change waiting on the coder's confirmation.
 *
 * `commit` runs the deferred server write; `revert` undoes the local state we
 * already applied. Reorders and in-place code edits apply locally *before* the
 * dialog opens so the coder sees the arrangement they just made — snapping it
 * back under an open dialog reads as refusal, and this feature discourages
 * rather than refuses. Nothing reaches the server until they say yes.
 */
interface PendingPrimarySeat {
  code: string;
  fitness: PrimaryDiagnosisFitness | null;
  commit: () => void | Promise<void>;
  revert: () => void;
}

interface VisitActiveDiagnosisProps {
  visitId: string;
  onActiveDiagnosesSaved?: () => void;
}

interface SortableRowProps {
  id: string;
  /** Diagnosis row id, for row-level contradiction jump targets (EMR-931). */
  diagnosisId: string;
  children: React.ReactNode;
}

const SortableRow: React.FC<SortableRowProps> = ({
  id,
  diagnosisId,
  children,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 50 : ('auto' as const),
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className='group'
      {...contradictionAnchor('diagnosis_list', diagnosisId)}
    >
      <div className='flex items-start gap-2 py-2 px-3 rounded-lg border bg-white'>
        {/* Drag handle */}
        <div
          {...attributes}
          {...listeners}
          className='mt-1 cursor-grab active:cursor-grabbing p-0.5 hover:bg-muted rounded shrink-0'
          title='Drag to reorder'
        >
          <GripVertical className='w-4 h-4 text-muted-foreground' />
        </div>
        <div className='flex-1 min-w-0'>{children}</div>
      </div>
    </div>
  );
};

export const VisitActiveDiagnosis: React.FC<VisitActiveDiagnosisProps> = ({
  visitId,
  onActiveDiagnosesSaved,
}) => {
  const { error: showError } = useToast();
  const [diagnoses, setDiagnoses] = useState<VisitDiagnosisResponse[]>([]);
  const [loading, setLoading] = useState(true);
  // Track temp IDs for optimistic creates
  const [pendingIds, setPendingIds] = useState<Set<string>>(new Set());
  const [pendingSeat, setPendingSeat] = useState<PendingPrimarySeat | null>(
    null
  );
  // Defaults assume PDGM until the episode says otherwise, so a slow or failed
  // fetch errs toward warning rather than toward silence.
  const [payerContext, setPayerContext] = useState({
    groupsViaPdgm: true,
    payerKnown: false,
  });

  const pdgmRelevant = isPdgmRelevant(payerContext);
  // The visit's own date: the picker offers, and the rows are judged by, the
  // codes valid that day. Set with the rows, so no search runs without it.
  const [visitDate, setVisitDate] = useState<string | undefined>(undefined);
  // False when no code set is loaded for the visit's date: the rows then carry
  // no verdict, and that must not read as "no problems".
  const [codesChecked, setCodesChecked] = useState(true);

  const fetchDiagnoses = useCallback(async () => {
    try {
      const response = await listVisitDiagnosesVisitsVisitIdDiagnosesGet({
        path: { visit_id: visitId },
      });
      if (response.data) {
        setDiagnoses(response.data.diagnoses);
        setPayerContext({
          groupsViaPdgm: response.data.groups_via_pdgm,
          payerKnown: response.data.payer_known,
        });
        setVisitDate(response.data.visit_date);
        setCodesChecked(response.data.codes_checked);
      }
    } catch (error) {
      console.error('Failed to fetch visit diagnoses:', error);
    } finally {
      setLoading(false);
    }
  }, [visitId]);

  useEffect(() => {
    fetchDiagnoses();
  }, [fetchDiagnoses]);

  const allDiagnoses = [...diagnoses].sort((a, b) => a.position - b.position);

  const isAtLimit = diagnoses.length >= MAX_TOTAL;

  // --- Handlers ---

  const handleFieldChange = async (
    diagnosisId: string,
    fields: {
      oe?: OEType;
      onset_date?: string | null;
      severity?: SeverityType;
    }
  ) => {
    // Build update payload, coercing null OE to empty string for backend's non-nullable `oe: str`
    const update: Record<string, unknown> = {};
    if (fields.oe !== undefined) update.oe = fields.oe ?? '';
    if (fields.onset_date !== undefined) update.onset_date = fields.onset_date;
    if (fields.severity !== undefined) update.severity = fields.severity;

    setDiagnoses(prev =>
      prev.map(d =>
        d.id === diagnosisId
          ? ({ ...d, ...update } as VisitDiagnosisResponse)
          : d
      )
    );

    if (pendingIds.has(diagnosisId)) return; // Don't send API for temp items

    try {
      const response = await updateVisitDiagnosisVisitsDiagnosesDiagnosisIdPut({
        path: { diagnosis_id: diagnosisId },
        body: update,
      });
      if (!response.error) {
        // Severity feeds the diagnosis severity rules (edits -5211/-5201), which
        // the scrubber recomputes server-side from the saved rows. Without this
        // the card for a rating the clinician just fixed sits there until some
        // unrelated edit happens to re-run validation (EMR-1280). The sibling
        // code/create/delete handlers already notify; this one was the gap.
        onActiveDiagnosesSaved?.();
      } else {
        console.error('Failed to update diagnosis:', response.error);
        fetchDiagnoses();
      }
    } catch (error) {
      console.error('Failed to update diagnosis:', error);
      fetchDiagnoses();
    }
  };

  /** True when this change needs the coder to look once before it is saved. */
  const needsConfirm = (
    fitness: PrimaryDiagnosisFitness | null | undefined,
    seatsPrimary: boolean
  ) => seatsPrimary && pdgmRelevant && shouldDiscourage(fitness);

  const handleUpdateCode = async (
    diagnosisId: string,
    code: string,
    description: string,
    fitness?: PrimaryDiagnosisFitness
  ) => {
    const previous = diagnoses;
    const target = diagnoses.find(d => d.id === diagnosisId);
    const nextFitness = fitness ?? null;

    setDiagnoses(prev =>
      prev.map(d =>
        d.id === diagnosisId
          ? {
              ...d,
              code,
              description,
              primary_fitness: nextFitness,
              // The picker searches billable codes only, so a picked code is
              // valid. Clear the old code's warning with it.
              invalid_reason: null,
            }
          : d
      )
    );

    if (pendingIds.has(diagnosisId)) {
      // The row's own create is still in flight, so there is no server id to
      // PUT against and the create's response would overwrite this edit anyway.
      // Undo the local change and say so, rather than showing an edit that
      // silently evaporates a moment later.
      setDiagnoses(previous);
      showError(
        'Still saving this diagnosis. Try the change again in a moment.'
      );
      return;
    }

    const save = async () => {
      // The generated client resolves on HTTP errors instead of throwing
      // (throwOnError is unset), so the response has to be inspected rather
      // than caught. Getting that wrong would let the confirm dialog attest a
      // write that never landed. The catch still earns its place: it covers a
      // genuine throw from the network stack or the saved-callback below.
      try {
        const response =
          await updateVisitDiagnosisVisitsDiagnosesDiagnosisIdPut({
            path: { diagnosis_id: diagnosisId },
            body: { code, description },
          });
        if (!response.error) {
          onActiveDiagnosesSaved?.();
        } else {
          console.error('Failed to update diagnosis code:', response.error);
          showError(
            getUserFacingApiMessage(response, 'Failed to update diagnosis')
          );
          fetchDiagnoses();
        }
      } catch (error) {
        console.error('Failed to update diagnosis code:', error);
        showError('Failed to update diagnosis');
        fetchDiagnoses();
      }
    };

    if (needsConfirm(nextFitness, Boolean(target?.is_primary))) {
      setPendingSeat({
        code,
        fitness: nextFitness,
        commit: save,
        revert: () => setDiagnoses(previous),
      });
      return;
    }
    await save();
  };

  const handleAddDiagnosis = async (
    code: string,
    description: string,
    fitness?: PrimaryDiagnosisFitness
  ) => {
    if (isAtLimit) {
      showError(`Maximum of ${MAX_TOTAL} diagnoses allowed per visit.`);
      return;
    }

    const hasPrimary = diagnoses.some(d => d.is_primary);
    const isPrimary = !hasPrimary;

    // Confirm before adding rather than after. Nothing has moved on screen yet,
    // so there is no arrangement to preserve — and a row that appears and then
    // vanishes on cancel would be the flash the deferred-write pattern exists
    // to avoid everywhere else.
    if (needsConfirm(fitness, isPrimary)) {
      setPendingSeat({
        code,
        fitness: fitness ?? null,
        commit: () => void createDiagnosis(code, description, fitness),
        revert: () => {},
      });
      return;
    }
    await createDiagnosis(code, description, fitness);
  };

  const createDiagnosis = async (
    code: string,
    description: string,
    fitness?: PrimaryDiagnosisFitness
  ) => {
    const hasPrimary = diagnoses.some(d => d.is_primary);
    const isPrimary = !hasPrimary;
    const position = isPrimary
      ? 0
      : Math.max(...diagnoses.map(d => d.position), -1) + 1;
    const tempId = crypto.randomUUID();

    // Optimistic add
    const tempDiagnosis: VisitDiagnosisResponse = {
      id: tempId,
      code,
      description,
      oe: '',
      onset_date: null,
      severity: null,
      is_primary: isPrimary,
      position,
      agency_id: '',
      visit_id: visitId,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      primary_fitness: fitness ?? null,
    };
    setDiagnoses(prev => [...prev, tempDiagnosis]);
    setPendingIds(prev => new Set(prev).add(tempId));

    try {
      const response = await createVisitDiagnosisVisitsVisitIdDiagnosesPost({
        path: { visit_id: visitId },
        body: {
          code,
          description,
          oe: '',
          onset_date: null,
          severity: null,
          is_primary: isPrimary,
          position,
        },
      });
      if (response.data) {
        // Replace temp with real
        setDiagnoses(prev =>
          prev.map(d => (d.id === tempId ? response.data! : d))
        );
        onActiveDiagnosesSaved?.();
      } else {
        // Remove temp on error
        setDiagnoses(prev => prev.filter(d => d.id !== tempId));
        showError(
          getUserFacingApiMessage(response, 'Failed to create diagnosis')
        );
      }
    } catch (error) {
      console.error('Failed to create diagnosis:', error);
      setDiagnoses(prev => prev.filter(d => d.id !== tempId));
      showError('Failed to create diagnosis');
    } finally {
      setPendingIds(prev => {
        const next = new Set(prev);
        next.delete(tempId);
        return next;
      });
    }
  };

  const handleRemoveDiagnosis = async (diagnosisId: string) => {
    const previous = diagnoses;
    const removed = diagnoses.find(d => d.id === diagnosisId);
    // Tie-break on code, matching both the claim pipeline's
    // (is_primary, position, code) sort and the server's promotion — the code
    // named in the confirm must be the code that ends up seated.
    const remaining = diagnoses
      .filter(d => d.id !== diagnosisId)
      .sort(
        (a, b) =>
          a.position - b.position || (a.code < b.code ? -1 : +(a.code > b.code))
      );

    // Deleting the primary is a fourth way a code reaches the primary seat, and
    // the only one where nobody chose it: the claim pipeline sorts by
    // (is_primary, position, code) and takes the first row, so with no primary
    // flagged the lowest-position secondary silently becomes the claim's
    // principal diagnosis. The server promotes that row inside the delete's own
    // transaction; what we owe the user is the same gate as every other reseat,
    // asked before the delete goes out.
    const promoted = removed?.is_primary ? remaining[0] : undefined;

    const applyRemoval = () =>
      setDiagnoses(
        remaining.map((d, idx) => ({
          ...d,
          position: idx,
          is_primary: promoted ? d.id === promoted.id : d.is_primary,
        }))
      );
    applyRemoval();

    if (pendingIds.has(diagnosisId)) return;

    const save = async () => {
      try {
        const response =
          await deleteVisitDiagnosisVisitsDiagnosesDiagnosisIdDelete({
            path: { diagnosis_id: diagnosisId },
          });
        if (response.error) {
          console.error('Failed to delete diagnosis:', response.error);
          showError(
            getUserFacingApiMessage(response, 'Failed to delete diagnosis')
          );
          fetchDiagnoses();
          return;
        }
        onActiveDiagnosesSaved?.();
        if (promoted) {
          // The server chose the heir and renumbered nothing. On a stale tab
          // that can be a different row than the one just named in the confirm,
          // so read back who is actually seated rather than showing our guess.
          fetchDiagnoses();
        }
      } catch (error) {
        console.error('Failed to delete diagnosis:', error);
        showError('Failed to delete diagnosis');
        fetchDiagnoses();
      }
    };

    if (promoted && needsConfirm(promoted.primary_fitness, true)) {
      setPendingSeat({
        code: promoted.code,
        fitness: promoted.primary_fitness ?? null,
        commit: save,
        revert: () => setDiagnoses(previous),
      });
      return;
    }
    await save();
  };

  const handleReorder = async (reordered: VisitDiagnosisResponse[]) => {
    const previous = diagnoses;
    const updatedWithPositions = reordered.map((d, idx) => ({
      ...d,
      position: idx,
    }));

    // A row whose create is still in flight carries a client-generated temp id
    // the server has never seen. Reordering would PUT that id — which the bulk
    // update matches to nothing while still applying the real primary's
    // demotion, leaving the visit with no primary at all.
    if (updatedWithPositions.some(d => pendingIds.has(d.id))) {
      setDiagnoses(previous);
      showError('Still saving a diagnosis. Reorder again in a moment.');
      return;
    }

    const newTop = updatedWithPositions[0];
    const primaryChanged = newTop && !newTop.is_primary;

    // Position 0 IS the primary, so any drop onto the top row reseats it.
    if (primaryChanged) {
      setDiagnoses(
        updatedWithPositions.map(d => ({
          ...d,
          is_primary: d.id === newTop.id,
        }))
      );
    } else {
      setDiagnoses(updatedWithPositions);
    }

    const save = async () => {
      // Response-checked, not try/caught: the generated client resolves on HTTP
      // errors. A confirmed reseat that silently 500s would leave the coder
      // looking at an unfit primary they believe they just committed.
      const response =
        await updateDiagnosisPositionsVisitsVisitIdDiagnosesPositionsPut({
          path: { visit_id: visitId },
          body: updatedWithPositions.map(d => ({
            id: d.id,
            position: d.position,
            ...(primaryChanged ? { is_primary: d.id === newTop.id } : {}),
          })),
        });
      if (response.error) {
        console.error('Failed to update positions:', response.error);
        showError('Failed to reorder diagnoses');
        fetchDiagnoses();
      }
    };

    if (primaryChanged && needsConfirm(newTop.primary_fitness, true)) {
      // The new order is already on screen; only the write waits.
      setPendingSeat({
        code: newTop.code,
        fitness: newTop.primary_fitness ?? null,
        commit: save,
        revert: () => setDiagnoses(previous),
      });
      return;
    }
    await save();
  };

  const handleSeatConfirm = async () => {
    const seat = pendingSeat;
    setPendingSeat(null);
    await seat?.commit();
  };

  const handleSeatCancel = () => {
    pendingSeat?.revert();
    setPendingSeat(null);
  };

  // --- Rendering ---

  if (loading) {
    return (
      <div className='flex items-center justify-center py-12'>
        <Loader2 className='w-6 h-6 animate-spin text-muted-foreground' />
      </div>
    );
  }

  return (
    <div className='space-y-6'>
      <div
        className='flex flex-col gap-2'
        {...contradictionAnchor('diagnosis_list')}
      >
        {/* Header */}
        <div className='text-xs font-semibold text-muted-foreground uppercase tracking-wide'>
          Diagnoses
        </div>
        {!codesChecked && (
          <p
            className='text-xs leading-snug text-destructive'
            data-testid='visit-codes-unchecked'
          >
            Codes could not be checked for this visit date.
          </p>
        )}

        {/* All diagnosis rows (draggable) */}
        {allDiagnoses.length > 0 && (
          <DraggableGrid
            items={allDiagnoses}
            onReorder={handleReorder}
            renderItem={dx => (
              <SortableRow key={dx.id} id={`dx-${dx.id}`} diagnosisId={dx.id}>
                <DiagnosisRowContent
                  diagnosis={dx}
                  pdgmRelevant={pdgmRelevant}
                  onFieldChange={fields => handleFieldChange(dx.id, fields)}
                  onUpdateCode={(code, desc, fitness) =>
                    handleUpdateCode(dx.id, code, desc, fitness)
                  }
                  onRemove={() => handleRemoveDiagnosis(dx.id)}
                  excludeCodes={diagnoses
                    .filter(d => d.id !== dx.id)
                    .map(d => d.code)}
                  asOf={visitDate}
                />
              </SortableRow>
            )}
            getItemId={dx => `dx-${dx.id}`}
            gridClassName='flex flex-col gap-2'
            sortingStrategy='vertical'
          />
        )}

        {/* Ghost slot */}
        {!isAtLimit && (
          <div className='flex items-start gap-2 py-2 px-3 rounded-lg border border-dashed bg-white'>
            {/* Spacer for drag handle alignment */}
            <div className='w-5 shrink-0' />
            <div className='flex-1 min-w-0'>
              <div className='flex flex-col gap-1.5'>
                <div className='flex items-center gap-2'>
                  <InlineDiagnosisSearch
                    onSelect={handleAddDiagnosis}
                    placeholder={
                      !diagnoses.some(d => d.is_primary)
                        ? 'Search to add primary diagnosis...'
                        : 'Search to add diagnosis...'
                    }
                    excludeCodes={diagnoses.map(d => d.code)}
                    // The ghost row seats the primary only while the list has
                    // none — the same condition its placeholder already reads.
                    primaryContext={!diagnoses.some(d => d.is_primary)}
                    pdgmRelevant={pdgmRelevant}
                    asOf={visitDate}
                  />
                </div>
                <InlineDiagnosisFields disabled />
              </div>
            </div>
          </div>
        )}
      </div>

      <Separator />

      {/* OASIS Active Diagnoses Comorbidities */}
      <OASISActiveDiagnoses visitId={visitId} onSave={onActiveDiagnosesSaved} />

      <PrimaryFitnessConfirmDialog
        code={pendingSeat?.code ?? null}
        fitness={pendingSeat?.fitness ?? null}
        onConfirm={handleSeatConfirm}
        onCancel={handleSeatCancel}
      />
    </div>
  );
};

// --- Row components ---

interface DiagnosisRowContentProps {
  diagnosis: VisitDiagnosisResponse;
  pdgmRelevant: boolean;
  onFieldChange: (fields: {
    oe?: OEType;
    onset_date?: string | null;
    severity?: SeverityType;
  }) => void;
  onUpdateCode: (
    code: string,
    description: string,
    fitness?: PrimaryDiagnosisFitness
  ) => void;
  onRemove?: () => void;
  excludeCodes?: string[];
  asOf?: string;
}

const DiagnosisRowContent: React.FC<DiagnosisRowContentProps> = ({
  diagnosis,
  pdgmRelevant,
  onFieldChange,
  onUpdateCode,
  onRemove,
  excludeCodes,
  asOf,
}) => {
  const onsetDate = parseISOToLocalDate(diagnosis.onset_date) ?? null;
  // Only the primary seat is judged; the flags mean nothing for a secondary.
  const warning = diagnosis.is_primary
    ? fitnessPresentation(diagnosis.primary_fitness, {
        softened: !pdgmRelevant,
      })
    : null;

  return (
    <div className='flex flex-col gap-1.5'>
      {/* Top line: label/position + code badge + search + remove */}
      <span
        className={`text-[10px] font-semibold uppercase tracking-wide shrink-0 ${
          diagnosis.is_primary ? 'text-primary' : 'text-muted-foreground'
        }`}
      >
        {diagnosis.is_primary ? 'Primary' : 'Secondary'}
      </span>
      <div className='flex items-center gap-2'>
        <InlineDiagnosisSearch
          description={diagnosis.description}
          onSelect={onUpdateCode}
          excludeCodes={excludeCodes}
          primaryContext={diagnosis.is_primary}
          pdgmRelevant={pdgmRelevant}
          asOf={asOf}
        />

        <Input
          className='font-mono w-fit max-w-32 text-center bg-primary-500 text-white'
          value={diagnosis.code}
          readOnly
        />

        {onRemove && (
          <button
            type='button'
            onClick={onRemove}
            className='p-1 rounded hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition-colors shrink-0'
            title='Remove diagnosis'
          >
            <X className='w-4 h-4' />
          </button>
        )}
      </div>

      {warning && (
        <p
          className={`text-xs leading-snug ${warning.toneClass}`}
          data-testid='primary-fitness-warning'
        >
          {warning.message}
        </p>
      )}

      {/* The primary's fitness already covers a deleted or header code, so
          validity is shown on secondaries only. */}
      {!diagnosis.is_primary && (
        <DiagnosisCodeValidityWarning reason={diagnosis.invalid_reason} />
      )}

      {/* Bottom line: O/E, Date, Severity fields */}
      <InlineDiagnosisFields
        oe={(diagnosis.oe as OEType) || null}
        onsetDate={onsetDate}
        onsetDateRaw={diagnosis.onset_date}
        severity={(diagnosis.severity as SeverityType) || null}
        onOEChange={oe => onFieldChange({ oe })}
        onOnsetDateChange={date =>
          onFieldChange({
            onset_date: date ? formatDateToISO(date) : null,
          })
        }
        onSeverityChange={severity => onFieldChange({ severity })}
      />
    </div>
  );
};

export default VisitActiveDiagnosis;
